// Data-driven navigation configuration for the Payroll UI left-hand navigation.
//
// The navigation tree has two structural levels rendered in the sidebar:
//   - Section  -> a non-navigating grouping/expansion node (Mango NavigationGroup).
//                 The "Home" section is the single exception: it is a direct
//                 destination with no child items.
//   - Item     -> a navigating destination (Mango NavigationItem) that routes to a page.
//
// A third conceptual level, Sub-item, is rendered as Mango tabs on the Item page
// (see TabbedItemPage). Sub-item tabs are URL-addressable: one route per tab.
//
// Future screens replace placeholders by changing a single `content` key here; the
// navigation structure and routing do not need to be restructured.

import { ADMIN_ROLE } from '../auth/store';

/** Identifies which screen renders for a destination. Defaults to a reusable placeholder. */
export type ContentKey = 'placeholder' | 'databaseConnectivity';

export interface NavSubItem {
  label: string;
  /** kebab-case slug derived from the label; the final URL segment for this tab. */
  slug: string;
  content: ContentKey;
}

export interface NavItem {
  label: string;
  /** kebab-case slug derived from the label; the URL segment for this item. */
  slug: string;
  /** Optional contextual note carried from the ticket (e.g. "Read-only panel"). */
  note?: string;
  /** When present, this item renders a tab strip; tabs are URL-addressable. */
  subItems?: NavSubItem[];
  /** Destination for a leaf item (no sub-items). Defaults to placeholder. */
  content?: ContentKey;
  /**
   * When true, the item is omitted from the left-hand navigation render but its route is
   * still built (reachable via deep link / in-app navigation). Used for the Worker Record
   * and Worker Form, which are reached from the Workers list rather than the nav (PRR-58).
   */
  hidden?: boolean;
  /**
   * PRR-316: when present, the item is omitted from the left-hand navigation render unless
   * the current session holds this role. This is UX only — every user-management API
   * endpoint independently enforces its own role check server-side regardless of what the
   * nav shows.
   */
  requiredRole?: string;
}

export interface NavSection {
  label: string;
  /** kebab-case slug; the first URL segment for items in this section. */
  slug: string;
  /** Mango icon name for the section header / direct item. */
  icon: string;
  /** Present only for the Home section: a direct destination, no child items. */
  home?: { content: ContentKey };
  /** Present for grouping sections. */
  items?: NavItem[];
}

/** Convert a human label into a stable kebab-case slug used in URLs. */
export const slugify = (label: string): string =>
  label
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const sub = (label: string, content: ContentKey = 'placeholder'): NavSubItem => ({ label, slug: slugify(label), content });
const item = (label: string, opts: { slug?: string; note?: string; content?: ContentKey; subItems?: NavSubItem[]; hidden?: boolean; requiredRole?: string } = {}): NavItem => ({
  label,
  slug: slugify(label),
  ...opts,
});

export const navSections: NavSection[] = [
  {
    label: 'Home',
    slug: '',
    icon: 'home',
    home: { content: 'databaseConnectivity' },
  },
  {
    label: 'Workers',
    slug: 'workers',
    icon: 'person',
    items: [
      item('Worker List'),
      // Worker Record is reached from the Workers list, not the left nav (PRR-58, AC-011), so it is
      // hidden but still routable. Per RR-Worker-Profile-V2 / PRD it has 11 top-level tabs; the
      // Person-layer data (Person Details, Contacts, Address, Emergency Contacts) renders as
      // sections inside the Personal tab (D-4), not as top-level tabs.
      item('Worker Record', {
        note: 'Reached from the Workers list',
        hidden: true,
        subItems: [
          'Overview', 'Personal', 'Pay', 'Tax', 'Banking', 'Super',
          'Leave', 'Employment', 'Documents', 'Notes', 'Activity',
        ].map((l) => sub(l)),
      }),
      // Worker add/edit page; reached from the Workers list ("Add a new worker" / row "Edit").
      item('Worker Form', { hidden: true, note: 'Reached from the Workers list' }),
      item('Termination Details', { note: 'Read-only panel' }),
      item('Employee Processes', {
        subItems: ['Onboarding', 'Workforce Sync', 'Bulk Pay Update', 'Compliance Tasks'].map((l) => sub(l)),
      }),
    ],
  },
  {
    label: 'Payroll',
    slug: 'payroll',
    icon: 'payroll-dollar',
    items: [
      item('Pay Runs'),
      item('STP Management', {
        note: 'Operational',
        subItems: ['Management', 'Reconciliation', 'Pay Events', 'Update Events', 'Finalisation', 'Full File Replacement'].map((l) => sub(l)),
      }),
      item('Superannuation', {
        subItems: ['Super Run Dashboard', 'Contribution Audit', 'Cap Tracker', 'SAFF Message Archive', 'Invariant Breach Queue'].map((l) => sub(l)),
      }),
    ],
  },
  {
    label: 'General Ledger',
    slug: 'general-ledger',
    icon: 'chart',
    items: [
      item('Overview'),
      item('Cost Centre'),
      item('Chart of Accounts'),
      item('Formulas'),
      item('Factors'),
      item('Rule'),
      item('Export'),
      item('Reporting'),
    ],
  },
  {
    label: 'Maintenance',
    slug: 'maintenance',
    icon: 'settings',
    items: [
      item('Organisation', {
        slug: 'organisations',
      }),
      item('STP Setup', {
        hidden: true,
        subItems: [{ ...sub('Payer & Readiness'), slug: 'payer-readiness' }, ...['Income Types', 'Tax Treatments', 'Disaggregation Map', 'Audit Log'].map((l) => sub(l))],
      }),
      item('Public Holidays'),
      item('Awards & Classifications', {
        subItems: ['Awards & Classifications', 'Rates'].map((l) => sub(l)),
      }),
      item('Pay Types'),
      item('Tax'),
      item('Leave'),
      item('Superannuation', {
        subItems: ['Scheme Configuration', 'Clearing House Configuration', 'System Parameters'].map((l) => sub(l)),
      }),
      item('Import Templates & Reports'),
      item('BSB Verification'),
    ],
  },
  {
    label: 'Administration',
    slug: 'administration',
    icon: 'lock',
    items: [
      item('Users & Access', { requiredRole: ADMIN_ROLE }),
      item('Security Roles'),
      item('Approval Workflows', { hidden: true }),
      item('General Parameters', { hidden: true }),
      item('Integration Settings', { hidden: true }),
      item('Payment Setup', { hidden: true }),
      item('Payroll Notes', { hidden: true, note: 'Renamed from "Significant Date Types"' }),
      item('Audit Log', { hidden: true }),
      item('Alerts & Notifications', { hidden: true }),
    ],
  },
];

/** Absolute route path for the Home destination. */
export const HOME_PATH = '/';

/** Absolute route path for an item (without leading slash collisions). */
export const itemPath = (section: NavSection, navItem: NavItem): string => `/${section.slug}/${navItem.slug}`;

/** Absolute route path for a sub-item tab. */
export const subItemPath = (section: NavSection, navItem: NavItem, subItem: NavSubItem): string =>
  `${itemPath(section, navItem)}/${subItem.slug}`;

export const LEGACY_REDIRECTS: ReadonlyArray<{ from: string; to: string }> = [
  { from: '/maintenance/configuration', to: '/maintenance/awards-and-classifications/awards-and-classifications' },
  { from: '/maintenance/configuration/awards-and-classifications', to: '/maintenance/awards-and-classifications/awards-and-classifications' },
  { from: '/maintenance/configuration/overview', to: '/maintenance/awards-and-classifications/awards-and-classifications' },
  { from: '/maintenance/configuration/rates', to: '/maintenance/awards-and-classifications/rates' },
  { from: '/maintenance/configuration/reviews', to: '/maintenance/awards-and-classifications/awards-and-classifications' },
  { from: '/maintenance/configuration/pay-types-assigned', to: '/maintenance/pay-types' },
  { from: '/maintenance/configuration/history', to: '/maintenance/awards-and-classifications/awards-and-classifications' },
  { from: '/maintenance/configuration/pay-templates', to: '/maintenance/import-templates-and-reports' },
  { from: '/maintenance/configuration/work-schedules', to: '/maintenance/awards-and-classifications/awards-and-classifications' },
  { from: '/maintenance/configuration/pay-types', to: '/maintenance/pay-types' },
  { from: '/maintenance/configuration/tax', to: '/maintenance/tax' },
  { from: '/maintenance/configuration/banking', to: '/maintenance/bsb-verification' },
  { from: '/administration/import-templates-and-opening-balances', to: '/maintenance/import-templates-and-reports' },
];

export interface ResolvedItem {
  section: NavSection;
  item: NavItem;
  path: string;
  /** First sub-item slug, when the item is tabbed. */
  firstSubSlug?: string;
}

/** Resolve a navigating item from its absolute route path (e.g. "/workers/worker-record"). */
export const resolveItemByPath = (path: string): ResolvedItem | undefined =>
  flattenItems().find((resolved) => resolved.path === path);

/** Every navigating item (excludes Home, which is handled separately). */
export const flattenItems = (): ResolvedItem[] =>
  navSections.flatMap((section) =>
    (section.items ?? []).map((navItem) => ({
      section,
      item: navItem,
      path: itemPath(section, navItem),
      firstSubSlug: navItem.subItems?.[0]?.slug,
    })),
  );

export interface ResolvedDestination {
  path: string;
  content: ContentKey;
  title: string;
}

/** Every concrete destination route (Home, leaf items, and each sub-item tab) with its content + title. */
export const flattenDestinations = (): ResolvedDestination[] => {
  const destinations: ResolvedDestination[] = [];
  for (const section of navSections) {
    if (section.home) {
      destinations.push({ path: HOME_PATH, content: section.home.content, title: section.label });
      continue;
    }
    for (const navItem of section.items ?? []) {
      if (navItem.subItems?.length)
        for (const subItem of navItem.subItems)
          destinations.push({ path: subItemPath(section, navItem, subItem), content: subItem.content, title: `${navItem.label} — ${subItem.label}` });
      else destinations.push({ path: itemPath(section, navItem), content: navItem.content ?? 'placeholder', title: navItem.label });
    }
  }
  return destinations;
};
